'use client';

import React, { useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Legend
} from 'recharts';

// Backend integration point: GET /api/hotels/:id/analytics?period=week
const weeklyData = [
  { day: 'Mon 14', court1: 6, court2: 4, court3: 8, revenue: 1170 },
  { day: 'Tue 15', court1: 4, court2: 3, court3: 5, revenue: 780 },
  { day: 'Wed 16', court1: 7, court2: 6, court3: 9, revenue: 1430 },
  { day: 'Thu 17', court1: 5, court2: 4, court3: 6, revenue: 975 },
  { day: 'Fri 18', court1: 10, court2: 9, court3: 11, revenue: 1950 },
  { day: 'Sat 19', court1: 12, court2: 11, court3: 12, revenue: 2275 },
  { day: 'Sun 20', court1: 8, court2: 7, court3: 9, revenue: 1560 },
];

const occupancyData = weeklyData.map((d) => ({
  ...d,
  total: d.court1 + d.court2 + d.court3,
  pct: Math.round(((d.court1 + d.court2 + d.court3) / 36) * 100),
}));

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-border rounded-xl shadow-card-hover p-3 min-w-[160px]">
      <p className="text-xs font-semibold text-foreground mb-2">{label}</p>
      {payload.map((p: any) => (
        <div key={`tt-${p.dataKey}`} className="flex justify-between gap-4 text-xs mb-1">
          <span className="text-muted-foreground">{p.name}</span>
          <span className="font-semibold font-mono tabular-nums" style={{ color: p.color }}>
            {p.value}{p.dataKey === 'pct' ? '%' : p.dataKey === 'revenue' ? ' TND' : ' sessions'}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function OccupancyChart() {
  const [chartType, setChartType] = useState<'occupancy' | 'revenue'>('occupancy');

  return (
    <div className="bg-white rounded-2xl border border-border shadow-card p-5">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h3 className="text-base font-semibold text-foreground">Weekly Court Performance</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Apr 14–20, 2026 · All 3 courts</p>
        </div>
        <div className="flex rounded-xl border border-border overflow-hidden">
          {(['occupancy', 'revenue'] as const).map((type) => (
            <button
              key={`chart-tab-${type}`}
              onClick={() => setChartType(type)}
              className={`px-4 py-2 text-xs font-semibold capitalize transition-colors ${
                chartType === type ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {type === 'occupancy' ? 'Occupancy %' : 'Revenue TND'}
            </button>
          ))}
        </div>
      </div>

      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'occupancy' ? (
            <AreaChart data={occupancyData} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
              <defs>
                <linearGradient id="occupancyGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} domain={[0, 100]} unit="%" />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="pct"
                name="Occupancy"
                stroke="hsl(var(--primary))"
                strokeWidth={2.5}
                fill="url(#occupancyGrad)"
              />
            </AreaChart>
          ) : (
            <BarChart data={occupancyData} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="court1" name="Court 1" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              <Bar dataKey="court2" name="Court 2" fill="hsl(145 58% 40%)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="court3" name="Court 3" fill="hsl(var(--secondary))" radius={[4, 4, 0, 0]} />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-4 gap-3 mt-4 pt-4 border-t border-border">
        {[
          { label: 'Peak Day', value: 'Saturday', sub: '92% occupancy' },
          { label: 'Slowest Day', value: 'Tuesday', sub: '44% occupancy' },
          { label: 'Total Sessions', value: '47', sub: 'this week' },
          { label: 'Week Revenue', value: '10,140', sub: 'TND' },
        ].map((item) => (
          <div key={`summary-${item.label}`}>
            <p className="text-xs text-muted-foreground">{item.label}</p>
            <p className="text-sm font-bold text-foreground font-mono tabular-nums">{item.value}</p>
            <p className="text-xs text-muted-foreground">{item.sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
}